import React, { memo, useEffect, useMemo } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import {
  BookmarkSimple,
  MapPin,
  Briefcase,
  WifiHigh,
  Clock,
  DotsThreeVertical,
  ShareNetwork,
  Buildings,
} from '@phosphor-icons/react';
import { generateJobUrl, Locale, toRelativePath } from '../../utils/routing/generate-url';
import { companyProfileUrlPath } from '../../utils/routing/company-profile-url-path';
import { BASE_URL } from '../../utils/constants/url-constants';
import { getDevSiteOrigin } from '../../utils/constants/dev-site-origin';
import { Link } from '../Link';
import { fixMojibake } from '../../utils/text/fix-mojibake';
import { CompanyLogo } from '../CompanyLogo';
import { CompanyName } from '../CompanyName';
import { trackJobClick, trackJobShare } from '../../utils/analytics/analytics';
import { LazyTooltip } from '../Tooltip';
import { MatchDonutWithLoginPrompt } from '../MatchDonut';
import { useHitShared } from './hooks/useHitShared';
import { ApplicationStateMenu } from '../application-state';
import { getLocationDisplayWithCount, getSinglePrioritizedLocation } from '../location/utils/locationUtils';
import { isDark } from './utils/brandColorUtils';
import { HitBenefits } from './components/HitBenefits';
import { sharePage as sharePageUtil } from '../../pages/job/@jobId/@seoSlug/utils/shareUtils';
import type { HitProps } from './types';
import { getHitFilterPillClasses } from '../../utils/filters/hit-filter-pill-classes';
import {
  STATE_BUTTON_COLOR_CLASSES,
  STATE_ICON_COLOR_CLASSES,
  getHitStateButtonSurfaceClass,
  getHitStateIconClass,
} from '../application-state/state-classes';
import { formatSrpSalaryDisplay } from '../../utils/data/format-job-card-labels';
import { SalaryInferenceExplainer } from '../SalaryInferenceExplainer';
import { resolveSalaryInferenceExplanationKind } from '../../utils/job-detail/resolve-salary-inference-explanation-kind';
import { formatSalaryInferenceExplanation } from '../../utils/job-detail/salary-inference-explanation-intl';
import { shouldLinkCompanyProfile } from '../../utils/company/should-link-company-profile';
import {
  resolveJobUrlCompany,
  resolveJobUrlLocation,
  resolveJobUrlRemoteness,
  resolveJobUrlTitle,
} from '../../utils/routing/job-url-input';

function hitCardFacetPillClass(attribute: string): string {
  return `${getHitFilterPillClasses(attribute)} min-w-0 text-xs 2lg:whitespace-nowrap 2lg:truncate 2lg:overflow-ellipsis whitespace-normal break-words`;
}

/**
 * Card variant of the Hit component
 * Displays job information in a vertical card layout
 */
export const HitCard: React.FC<HitProps> = memo(({ hit, sendEvent, index }) => {
  const intl = useIntl();
  const shared = useHitShared({ hit, sendEvent });
  const menuButtonRef = React.useRef<HTMLButtonElement>(null);

  const salaryInferenceKind = useMemo(
    () =>
      resolveSalaryInferenceExplanationKind({
        salaryIsEstimate: hit.salaryIsEstimate,
        salaryRangeMin: hit.salaryRangeMin,
        salaryRangeMax: hit.salaryRangeMax,
        salaryDetails: hit.salaryDetails,
      }),
    [hit.salaryIsEstimate, hit.salaryRangeMin, hit.salaryRangeMax, hit.salaryDetails],
  );
  const salaryInferenceText = useMemo(
    () => formatSalaryInferenceExplanation(salaryInferenceKind, intl.formatMessage),
    [salaryInferenceKind, intl],
  );
  
  const {
    jobId,
    jobUrl,
    articleId,
    titleId,
    jobTitle,
    isSaved,
    isApplied,
    isHidden,
    currentApplicationState,
    isToolsPage,
    savedJobPayload,
    brandColour,
    useBrandColour,
    leftBorderStyle,
    matchScore,
    matchScoreData,
    matchScoreMeta,
    isAuthenticated,
    onLoginClick,
    actions,
    getUrlForIndexUiState,
    pushIndexUiState,
    makeRefinementState,
    showToast,
    browsedJobRef,
    menuRefDesktop,
    isMenuOpen,
    setIsMenuOpen,
    buttonState,
    locationData,
    locationStr,
    filters,
  } = shared;

  // Track view event - use hit.objectID instead of hit object to avoid unnecessary re-runs
  const hitId = hit.objectID || hit.id;
  useEffect(() => {
    if (sendEvent) {
      sendEvent('view', hit, 'Job Viewed (CA English)');
    }
  }, [sendEvent, hitId]);

  // Memoize filter parsing to avoid JSON.parse on every render
  const parsedFilters = useMemo(() => {
    if (typeof filters === 'string') {
      try {
        return JSON.parse(filters) as Record<string, string[]>;
      } catch {
        return {};
      }
    }
    return (filters || {}) as Record<string, string[]>;
  }, [filters]);

  // Memoize expensive location calculations
  const userLocationFilter = useMemo(() => parsedFilters.location, [parsedFilters.location]);
  const singleLocation = useMemo(
    () => getSinglePrioritizedLocation(hit, userLocationFilter),
    [hit, userLocationFilter]
  );
  const cardLocationData = useMemo(() => getLocationDisplayWithCount(hit), [hit]);
  const hasMultipleLocations = useMemo(
    () => cardLocationData.allLocations.length > 1,
    [cardLocationData.allLocations.length]
  );

  const companyProfileHref = useMemo(
    () =>
      shouldLinkCompanyProfile({
        companyId: hit.companyId,
        isTyomarkkinatori: hit.isTyomarkkinatori,
      }) && hit.companyId
        ? companyProfileUrlPath(intl.locale as 'en' | 'fi' | 'sv', hit.companyId)
        : '#',
    [hit.companyId, hit.isTyomarkkinatori, intl.locale],
  );
  const canLinkCompanyProfile = companyProfileHref !== '#';

  return (
    <>
      {/* Unified Card Layout with Responsive Tweaks */}
      <article
        id={articleId}
        ref={browsedJobRef}
        className={`job-listing bg-white dark:bg-gray-800 rounded-3xl shadow border border-gray-100 dark:border-gray-800 p-3 sm:p-3 md:p-6 lg:p-8 flex flex-col h-full ${isMenuOpen ? 'relative z-[9998] overflow-visible' : ''}`}
        title={jobTitle}
        aria-labelledby={titleId}
        style={{
          ...leftBorderStyle,
        }}
      >
        {/* Header: Match Donut (top left), Logo, Company Name (centered relative to card) */}
        <div className="relative pb-1 sm:pb-1.5 md:pb-2 min-w-0">
          {/* Match Score Donut - Top Left (absolute positioned) */}
          <div className="absolute top-0 left-0 flex-shrink-0 z-10">
            <MatchDonutWithLoginPrompt
              score={matchScore}
              size={40}
              onLoginClick={onLoginClick}
              breakdown={matchScoreData ?? undefined}
              isAuthenticated={isAuthenticated}
              matchScoreMeta={matchScoreMeta}
            />
          </div>
          {/* Logo + company name — one link; hover/focus + cue make the company profile obvious */}
          <div className="flex flex-col items-center gap-1 sm:gap-1.5 md:gap-2 min-w-0 max-w-full px-1">
            {canLinkCompanyProfile ? (
              <Link
                href={companyProfileHref}
                className="hit-card__company-link group flex max-w-full flex-col items-center gap-1 sm:gap-1.5 md:gap-2 no-underline"
                aria-label={intl.formatMessage(
                  {
                    id: 'hit.companyProfile.aria',
                    defaultMessage: 'View company profile, hiring data, and insights for {company}',
                    description: 'Accessibility label for the employer logo and name link on a job card',
                  },
                  { company: hit.companyName ?? hit.companyId ?? '' },
                )}
                title={intl.formatMessage(
                  {
                    id: 'hit.companyLinkTitle',
                    defaultMessage: 'Open company profile — data and insights for {company}',
                    description: 'Tooltip for the employer logo and name link on a job card',
                  },
                  { company: hit.companyName ?? hit.companyId ?? '' },
                )}
              >
                <span className="hit-card__company-logo-wrap">
                  <CompanyLogo
                    companyName={hit.companyId ?? ''}
                    initialsSourceName={hit.companyName ?? hit.companyId}
                    registryCompanyFormDescription={hit.registryCompanyFormDescription}
                    isTyomarkkinatori={hit.isTyomarkkinatori}
                    size={56}
                    loading={index !== undefined && index < 3 ? 'eager' : 'lazy'}
                  />
                </span>
                {hit.companyId ? (
                  <>
                    <span className="break-words text-center font-bold text-sm leading-tight text-gray-900 underline decoration-from-font underline-offset-2 transition-colors group-hover:text-[#3B5E45] dark:text-gray-100 dark:group-hover:text-[#98CFA8] sm:text-base">
                      <CompanyName
                        name={hit.companyId}
                        className="font-bold text-gray-900 dark:text-gray-100 text-sm sm:text-base"
                      />
                    </span>
                    <span className="hit-card__company-cue" aria-hidden>
                      <Buildings className="h-3 w-3 shrink-0 opacity-90" aria-hidden weight="duotone" />
                      <FormattedMessage
                        id="hit.companyInsights.cue"
                        defaultMessage="Company insights"
                        description="Short label under the employer name on a job card; the block links to the company profile"
                      />
                    </span>
                  </>
                ) : null}
              </Link>
            ) : (
              <div className="flex max-w-full flex-col items-center gap-1 sm:gap-1.5 md:gap-2 no-underline">
                <span className="hit-card__company-logo-wrap">
                  <CompanyLogo
                    companyName={hit.companyId ?? ''}
                    initialsSourceName={hit.companyName ?? hit.companyId}
                    registryCompanyFormDescription={hit.registryCompanyFormDescription}
                    isTyomarkkinatori={hit.isTyomarkkinatori}
                    size={56}
                    loading={index !== undefined && index < 3 ? 'eager' : 'lazy'}
                  />
                </span>
                {hit.companyId ? (
                  <span className="break-words text-center font-bold text-sm leading-tight text-gray-900 dark:text-gray-100 sm:text-base">
                    <CompanyName
                      name={hit.companyId}
                      className="font-bold text-gray-900 dark:text-gray-100 text-sm sm:text-base"
                    />
                  </span>
                ) : null}
              </div>
            )}
          </div>
        </div>
        <hr className="my-1 sm:my-2 border-gray-200 dark:border-gray-700" />
        {/* Salary Section - use SSR-precomputed label when present to avoid toLocaleString in render */}
        <div className="flex flex-col items-center gap-0.5 sm:gap-1 pb-1 sm:pb-2 min-w-0">
          <div className="mt-0.5 sm:mt-1 mb-0.5 sm:mb-1 flex items-center justify-center gap-1 text-xs sm:text-sm text-gray-700 dark:text-gray-200 font-semibold min-w-0">
            <SalaryInferenceExplainer explanationText={salaryInferenceText} className="text-center min-w-0">
              {hit._salaryLabel ? (
                <span className="break-words">{hit._salaryLabel}</span>
              ) : typeof hit.salaryRangeMin === 'number' && typeof hit.salaryRangeMax === 'number' && hit.salaryRangeMin > 0 && hit.salaryRangeMax > 0 ? (
                <span className="break-words">
                  {formatSrpSalaryDisplay({
                    salaryRangeMin: hit.salaryRangeMin,
                    salaryRangeMax: hit.salaryRangeMax,
                    salaryIsEstimate: hit.salaryIsEstimate,
                    locale: intl.locale,
                  })}
                </span>
              ) : (
                <span className="text-gray-400 dark:text-gray-500 font-normal">
                  <FormattedMessage id="hit.salaryNotSpecified" defaultMessage="Salary not specified" description="Shown when a job listing does not specify a salary range" />
                </span>
              )}
            </SalaryInferenceExplainer>
          </div>
        </div>
        <hr className="my-1 sm:my-2 border-gray-200 dark:border-gray-700" />
        {/* Meta Section: Location, Remoteness, JobType, RoleLevel */}
        <div className="bg-gray-50 dark:bg-gray-900/60 rounded-xl px-2 sm:px-3 py-1.5 sm:py-2 mt-1 sm:mt-2 mb-1 sm:mb-2">
          {hasMultipleLocations ? (
            // Multiple locations: use single prioritized location with "+X more"
            <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm text-gray-600 dark:text-gray-300 min-w-0 justify-between">
              {/* Location */}
              <div className="flex items-center gap-1 min-w-0 pr-2">
                <MapPin className="h-4 w-4 flex-shrink-0 opacity-80 text-jc-filter-location-text" />
                {singleLocation.display ? (
                  <Link
                    href={getUrlForIndexUiState(makeRefinementState('location', singleLocation.locationForUrl))}
                    className={hitCardFacetPillClass('location')}
                    title={intl.formatMessage({ id: 'hit.locationLinkTitle', defaultMessage: 'Search for jobs in {location}', description: 'Title attribute for location filter link in job card' }, { location: singleLocation.fullList })}
                    aria-label={intl.formatMessage({ id: 'hit.locationLinkAria', defaultMessage: 'Search for jobs in {location}', description: 'Aria label for location filter link in job card' }, { location: singleLocation.display })}
                    onClick={(e) => {
                      e.preventDefault();
                      if (singleLocation.locationForUrl) pushIndexUiState(makeRefinementState('location', singleLocation.locationForUrl));
                    }}
                  >
                    {fixMojibake(singleLocation.display)}
                  </Link>
                ) : (
                  <span className="text-gray-400 italic text-xs font-medium">
                    <FormattedMessage id="hit.locationNotAvailable" defaultMessage="Location not available" description="Shown when job location information is not available in the job card" />
                  </span>
                )}
              </div>
              {/* Remoteness */}
              <div className="flex items-center gap-1 min-w-0 pr-2">
                <WifiHigh className="h-4 w-4 flex-shrink-0 opacity-80 text-jc-filter-location-text" />
                {hit.remoteness && hit.remoteness.trim() !== '' ? (
                  <Link
                    href={getUrlForIndexUiState(makeRefinementState('remoteness', hit.remoteness ?? ''))}
                    className={hitCardFacetPillClass('remoteness')}
                    title={intl.formatMessage({ id: 'hit.remotenessLinkTitle', defaultMessage: 'Search for {remoteness} jobs', description: 'Title attribute for remoteness filter link in job card' }, { remoteness: hit.remoteness })}
                    aria-label={intl.formatMessage({ id: 'hit.remotenessLinkAria', defaultMessage: 'Search for {remoteness} jobs', description: 'Aria label for remoteness filter link in job card' }, { remoteness: hit.remoteness })}
                    onClick={e => {
                      e.preventDefault();
                      pushIndexUiState(makeRefinementState('remoteness', hit.remoteness ?? ''));
                    }}
                  >
                    {fixMojibake(hit.remoteness ?? '')}
                  </Link>
                ) : (
                  <span className="text-gray-300 dark:text-gray-600">—</span>
                )}
              </div>
              {/* Job Type */}
              <div className="flex items-center gap-1 min-w-0 pl-2">
                <Briefcase className="h-4 w-4 flex-shrink-0 opacity-80 text-jc-filter-job-text" />
                {hit.jobType && hit.jobType.trim() !== '' ? (
                  <Link
                    href={getUrlForIndexUiState(makeRefinementState('jobType', hit.jobType ?? ''))}
                    className={hitCardFacetPillClass('jobType')}
                    title={intl.formatMessage({ id: 'hit.jobTypeLinkTitle', defaultMessage: 'Search for {jobType} jobs', description: 'Title attribute for job type filter link in job card' }, { jobType: hit.jobType })}
                    aria-label={intl.formatMessage({ id: 'hit.jobTypeLinkAria', defaultMessage: 'Search for {jobType} jobs', description: 'Aria label for job type filter link in job card' }, { jobType: hit.jobType })}
                    onClick={e => {
                      e.preventDefault();
                      pushIndexUiState(makeRefinementState('jobType', hit.jobType ?? ''));
                    }}
                  >
                    {fixMojibake(hit.jobType ?? '')}
                  </Link>
                ) : (
                  <span className="text-gray-300 dark:text-gray-600">—</span>
                )}
              </div>
              {/* Role Level */}
              <div className="flex items-center gap-1 min-w-0 pr-2">
                {hit.normalisedRoleLevel && hit.normalisedRoleLevel.trim() !== '' ? (
                  <Link
                    href={getUrlForIndexUiState(makeRefinementState('normalisedRoleLevel', hit.normalisedRoleLevel ?? ''))}
                    className={hitCardFacetPillClass('normalisedRoleLevel')}
                    title={intl.formatMessage({ id: 'hit.roleLevelLinkTitle', defaultMessage: 'Search for {roleLevel} jobs', description: 'Title attribute for role level filter link in job card' }, { roleLevel: hit.normalisedRoleLevel })}
                    aria-label={intl.formatMessage({ id: 'hit.roleLevelLinkAria', defaultMessage: 'Search for {roleLevel} jobs', description: 'Aria label for role level filter link in job card' }, { roleLevel: hit.normalisedRoleLevel })}
                    onClick={e => {
                      e.preventDefault();
                      pushIndexUiState(makeRefinementState('normalisedRoleLevel', hit.normalisedRoleLevel ?? ''));
                    }}
                  >
                    <FormattedMessage
                      id="hit.roleLevelLinkLabel"
                      defaultMessage="{roleLevel} jobs"
                      values={{ roleLevel: fixMojibake(hit.normalisedRoleLevel ?? '') }}
                      description="Link label to filter jobs by role level (e.g., 'Senior jobs', 'Junior jobs')"
                    />
                  </Link>
                ) : (
                  <span className="text-gray-300 dark:text-gray-600">—</span>
                )}
              </div>
            </div>
          ) : (
            // Single location: all pills in grid
            <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm text-gray-600 dark:text-gray-300 min-w-0 justify-between">
              {/* First row: Location, Remoteness */}
              <div className="flex items-center gap-1 min-w-0 pr-2">
                <MapPin className="h-4 w-4 flex-shrink-0 opacity-80 text-jc-filter-location-text" />
                {singleLocation.display || cardLocationData.display ? (
                  <Link
                    href={getUrlForIndexUiState(makeRefinementState('location', singleLocation.locationForUrl || locationStr))}
                    className={hitCardFacetPillClass('location')}
                    title={intl.formatMessage({ id: 'hit.locationLinkTitle', defaultMessage: 'Search for jobs in {location}', description: 'Title attribute for location filter link in job card' }, { location: singleLocation.fullList || cardLocationData.fullList })}
                    aria-label={intl.formatMessage({ id: 'hit.locationLinkAria', defaultMessage: 'Search for jobs in {location}', description: 'Aria label for location filter link in job card' }, { location: singleLocation.display || cardLocationData.display })}
                    onClick={(e) => {
                      e.preventDefault();
                      if (singleLocation.locationForUrl || locationStr) pushIndexUiState(makeRefinementState('location', singleLocation.locationForUrl || locationStr));
                    }}
                  >
                    {fixMojibake(singleLocation.display || cardLocationData.display)}
                  </Link>
                ) : (
                  <span className="text-gray-400 italic text-xs font-medium">
                    <FormattedMessage id="hit.locationNotAvailable" defaultMessage="Location not available" description="Shown when job location information is not available in the job card" />
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1 min-w-0 pl-2">
                <WifiHigh className="h-4 w-4 flex-shrink-0 opacity-80 text-jc-filter-location-text" />
                {hit.remoteness && hit.remoteness.trim() !== '' ? (
                  <Link
                    href={getUrlForIndexUiState(makeRefinementState('remoteness', hit.remoteness ?? ''))}
                    className={hitCardFacetPillClass('remoteness')}
                    title={intl.formatMessage({ id: 'hit.remotenessLinkTitle', defaultMessage: 'Search for {remoteness} jobs', description: 'Title attribute for remoteness filter link in job card' }, { remoteness: hit.remoteness })}
                    aria-label={intl.formatMessage({ id: 'hit.remotenessLinkAria', defaultMessage: 'Search for {remoteness} jobs', description: 'Aria label for remoteness filter link in job card' }, { remoteness: hit.remoteness })}
                    onClick={e => {
                      e.preventDefault();
                      pushIndexUiState(makeRefinementState('remoteness', hit.remoteness ?? ''));
                    }}
                  >
                    {fixMojibake(hit.remoteness ?? '')}
                  </Link>
                ) : (
                  <span className="text-gray-300 dark:text-gray-600">—</span>
                )}
              </div>
              {/* Second row: JobType, RoleLevel */}
              <div className="flex items-center gap-1 min-w-0 pr-2">
                <Briefcase className="h-4 w-4 flex-shrink-0 opacity-80 text-jc-filter-job-text" />
                {hit.jobType && hit.jobType.trim() !== '' ? (
                  <Link
                    href={getUrlForIndexUiState(makeRefinementState('jobType', hit.jobType ?? ''))}
                    className={hitCardFacetPillClass('jobType')}
                    title={intl.formatMessage({ id: 'hit.jobTypeLinkTitle', defaultMessage: 'Search for {jobType} jobs', description: 'Title attribute for job type filter link in job card' }, { jobType: hit.jobType })}
                    aria-label={intl.formatMessage({ id: 'hit.jobTypeLinkAria', defaultMessage: 'Search for {jobType} jobs', description: 'Aria label for job type filter link in job card' }, { jobType: hit.jobType })}
                    onClick={e => {
                      e.preventDefault();
                      pushIndexUiState(makeRefinementState('jobType', hit.jobType ?? ''));
                    }}
                  >
                    {fixMojibake(hit.jobType ?? '')}
                  </Link>
                ) : (
                  <span className="text-gray-300 dark:text-gray-600">—</span>
                )}
              </div>
              <div className="flex items-center gap-1 min-w-0 pl-2">
                {hit.normalisedRoleLevel && hit.normalisedRoleLevel.trim() !== '' ? (
                  <Link
                    href={getUrlForIndexUiState(makeRefinementState('normalisedRoleLevel', hit.normalisedRoleLevel ?? ''))}
                    className={hitCardFacetPillClass('normalisedRoleLevel')}
                    title={intl.formatMessage({ id: 'hit.roleLevelLinkTitle', defaultMessage: 'Search for {roleLevel} jobs', description: 'Title attribute for role level filter link in job card' }, { roleLevel: hit.normalisedRoleLevel })}
                    aria-label={intl.formatMessage({ id: 'hit.roleLevelLinkAria', defaultMessage: 'Search for {roleLevel} jobs', description: 'Aria label for role level filter link in job card' }, { roleLevel: hit.normalisedRoleLevel })}
                    onClick={e => {
                      e.preventDefault();
                      pushIndexUiState(makeRefinementState('normalisedRoleLevel', hit.normalisedRoleLevel ?? ''));
                    }}
                  >
                    <FormattedMessage
                      id="hit.roleLevelLinkLabel"
                      defaultMessage="{roleLevel} jobs"
                      values={{ roleLevel: fixMojibake(hit.normalisedRoleLevel ?? '') }}
                      description="Link label to filter jobs by role level (e.g., 'Senior jobs', 'Junior jobs')"
                    />
                  </Link>
                ) : (
                  <span className="text-gray-300 dark:text-gray-600">—</span>
                )}
              </div>
            </div>
          )}
        </div>
        {/* Benefits Section */}
        {hit.benefits && hit.benefits.length > 0 && (
          <HitBenefits benefits={hit.benefits} maxItems={3} />
        )}
        <hr className="my-2 border-gray-200 dark:border-gray-700" />
        {/* Content wrapper - allows buttons to be pushed to bottom on mobile */}
        <div className="flex flex-col flex-1 min-h-0">
          {/* Title and Normalised Title - Always visible */}
          <div className="flex flex-col gap-2">
            {/* Title Row - Full Width */}
            {hit.title && (
              <h3 id={titleId} className="text-base sm:text-lg md:text-xl font-bold text-gray-900 dark:text-gray-100 leading-tight break-words w-full">
                {fixMojibake(hit.title ?? '')}
              </h3>
            )}
            {/* Normalised Title Row - Full Width (as subtitle) */}
            {hit.normalisedTitle && (
              <div className="text-[11px] sm:text-xs text-gray-400 dark:text-gray-500 font-normal italic break-words w-full">
                {fixMojibake(hit.normalisedTitle ?? '')}
              </div>
            )}
          </div>
          {/* Buttons Row - On own row at bottom on sm/md, after title on lg+ */}
          <div className="flex items-center gap-3 lg:gap-4 w-full sm:w-full md:w-full lg:w-auto mt-auto lg:mt-6" ref={menuRefDesktop}>
            <LazyTooltip
              content={isSaved
                ? intl.formatMessage({ id: 'hit.tooltip.saved', defaultMessage: 'Remove from saved jobs', description: 'Tooltip text for save button when job is already saved, indicating clicking will remove it from saved jobs' })
                : intl.formatMessage({ id: 'hit.tooltip.save', defaultMessage: 'Save this job to compare with others', description: 'Tooltip text for save button when job is not saved, indicating clicking will save the job' })}
              position="top"
            >
              <button
                className={`backdrop-blur border rounded-[18px] w-11 h-11 lg:w-10 lg:h-10 flex items-center justify-center shadow transition cursor-pointer ${
                  isSaved
                    ? ''
                    : `${STATE_BUTTON_COLOR_CLASSES.default} hover:bg-jc-elevated-hover`
                }`}
                aria-label={
                  isSaved
                    ? intl.formatMessage({ id: 'hit.saved', defaultMessage: 'Saved', description: 'Button label when job is saved' })
                    : intl.formatMessage({ id: 'hit.save', defaultMessage: 'Save', description: 'Button label to save a job' })
                }
                style={isSaved ? {
                  backgroundColor: useBrandColour && brandColour ? brandColour : '#C8DDCE',
                  borderColor: useBrandColour && brandColour ? brandColour : '#C8DDCE',
                } : {}}
                onMouseOver={isSaved ? (e: React.MouseEvent<HTMLButtonElement>) => {
                  if (useBrandColour && brandColour) {
                    e.currentTarget.style.backgroundColor = brandColour;
                    e.currentTarget.style.borderColor = brandColour;
                    e.currentTarget.style.filter = 'brightness(0.92)';
                  } else {
                    e.currentTarget.style.backgroundColor = '#B6D4BC';
                    e.currentTarget.style.borderColor = '#B6D4BC';
                  }
                } : undefined}
                onMouseOut={isSaved ? (e: React.MouseEvent<HTMLButtonElement>) => {
                  if (useBrandColour && brandColour) {
                    e.currentTarget.style.backgroundColor = brandColour;
                    e.currentTarget.style.borderColor = brandColour;
                    e.currentTarget.style.filter = '';
                  } else {
                    e.currentTarget.style.backgroundColor = '#C8DDCE';
                    e.currentTarget.style.borderColor = '#C8DDCE';
                  }
                } : undefined}
                onClick={actions.handleToggleSaved}
                type="button"
              >
                {isSaved ? (
                  <BookmarkSimple weight="fill" className="h-6 w-6 lg:h-5 lg:w-5" style={{ color: useBrandColour && brandColour ? (isDark(brandColour) ? '#fff' : '#1E1A14') : '#1E1A14' }} />
                ) : (
                  <BookmarkSimple className={`h-6 w-6 lg:h-5 lg:w-5 ${STATE_ICON_COLOR_CLASSES.default}`} />
                )}
              </button>
            </LazyTooltip>
            
            {/* Ellipses Menu - State Change Button */}
            <div className="relative z-10">
              <LazyTooltip
                content={buttonState 
                  ? buttonState.label
                  : intl.formatMessage({ id: 'hit.tooltip.moreOptions', defaultMessage: 'More options' })}
                position="top"
                showOnFocus={false}
              >
                <button
                  ref={menuButtonRef}
                  className={`backdrop-blur border rounded-full ${
                    buttonState ? 'w-auto px-2' : 'w-11 lg:w-10'
                  } h-11 lg:h-10 flex items-center gap-1 justify-center shadow transition cursor-pointer ${
                    buttonState ? getHitStateButtonSurfaceClass(buttonState.color) : STATE_BUTTON_COLOR_CLASSES.default
                  } hover:bg-jc-elevated-hover`}
                  aria-label={buttonState 
                    ? buttonState.label
                    : intl.formatMessage({ id: 'hit.moreOptions', defaultMessage: 'More options' })}
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  type="button"
                >
                  {buttonState ? (() => {
                    const IconComponent = buttonState.icon;
                    return (
                      <>
                        <IconComponent className={`h-6 w-6 lg:h-5 lg:w-5 ${getHitStateIconClass(buttonState.color)}`} />
                        <DotsThreeVertical className={`h-6 w-6 lg:h-5 lg:w-5 ${STATE_ICON_COLOR_CLASSES.default}`} />
                      </>
                    );
                  })() : (
                    <DotsThreeVertical className={`h-6 w-6 lg:h-5 lg:w-5 ${STATE_ICON_COLOR_CLASSES.default}`} />
                  )}
                </button>
              </LazyTooltip>
              
              {/* Dropdown Menu */}
              {isMenuOpen && (
                <ApplicationStateMenu
                  currentApplicationState={currentApplicationState}
                  isApplied={isApplied}
                  isHidden={isHidden}
                  onSetApplicationState={actions.handleSetApplicationState}
                  onToggleApplied={actions.handleToggleApplied}
                  onToggleHide={actions.handleToggleHide}
                  onClose={() => setIsMenuOpen(false)}
                  triggerRef={menuButtonRef}
                  usePortal={true}
                />
              )}
            </div>
            
            <LazyTooltip
              content={intl.formatMessage({ id: 'hit.tooltip.share', defaultMessage: 'Share this job', description: 'Tooltip text for share button that copies the job link to clipboard or opens native share dialog' })}
              position="top"
            >
              <button
                className={`backdrop-blur rounded-full w-11 h-11 lg:w-10 lg:h-10 flex items-center justify-center shadow transition cursor-pointer ${STATE_BUTTON_COLOR_CLASSES.default} hover:bg-jc-elevated-hover`}
                aria-label={intl.formatMessage({ id: 'hit.share', defaultMessage: 'Share', description: 'Button label to share a job' })}
                onClick={async () => {
                  trackJobShare({
                    jobId: hit.objectID,
                    jobTitle: hit.normalisedTitle || hit.title || '',
                    locale: intl.locale,
                  });
                  
                  const shareJobUrl = generateJobUrl({
                    baseUrl: import.meta.env.DEV ? getDevSiteOrigin() : BASE_URL,
                    jobId: hit.objectID.toString(),
                    locale: intl.locale as Locale,
                    title: resolveJobUrlTitle(hit),
                    company: resolveJobUrlCompany(hit),
                    location: resolveJobUrlLocation(hit),
                    remoteness: resolveJobUrlRemoteness(hit),
                  });
                  
                  const shareJobTitle = hit.normalisedTitle || hit.title || document.title;
                  
                  await sharePageUtil(
                    shareJobUrl,
                    shareJobTitle,
                    () => {
                      showToast(
                        intl.formatMessage({
                          id: 'hit.linkCopied',
                          defaultMessage: 'Link copied to clipboard!',
                          description: 'Message shown when link is copied to clipboard',
                        }),
                        'success'
                      );
                    },
                    (error) => {
                      showToast(
                        intl.formatMessage({
                          id: 'hit.shareError',
                          defaultMessage: 'Something went wrong while trying to share this job.',
                          description: "Message shown when there's an error sharing",
                        }),
                        'error'
                      );
                    }
                  );
                }}
                type="button"
              >
                <ShareNetwork className={`h-6 w-6 lg:h-5 lg:w-5 ${STATE_ICON_COLOR_CLASSES.default}`} />
              </button>
            </LazyTooltip>
            <LazyTooltip
              content={intl.formatMessage({ id: 'hit.tooltip.details', defaultMessage: 'View full job details and description', description: 'Tooltip text for button/link that opens the full job detail page with complete job description' })}
              position="top"
            >
              <Link
                className="flex-1 ml-2 block text-center px-4 py-2.5 lg:px-4 lg:py-2 text-base font-bold rounded-xl shadow backdrop-blur transition focus:outline-none focus:ring-2 focus:ring-green-400 min-h-[44px] lg:min-h-0 flex items-center justify-center"
                style={{
                  letterSpacing: '0.01em',
                  backgroundColor: useBrandColour && brandColour ? brandColour : '#bbf7d0',
                  color: useBrandColour && brandColour ? (isDark(brandColour) ? '#fff' : '#111827') : '#111827',
                  border: 'none',
                }}
                href={toRelativePath(generateJobUrl({ 
                  baseUrl: '', 
                  jobId: hit.objectID.toString(), 
                  locale: intl.locale as Locale, 
                  title: resolveJobUrlTitle(hit), 
                  company: resolveJobUrlCompany(hit), 
                  location: resolveJobUrlLocation(hit), 
                  remoteness: resolveJobUrlRemoteness(hit) 
                }))}
                onClick={(e) => {
                  if (sendEvent) {
                    sendEvent('click', hit, 'Job Details Clicked');
                    sendEvent('conversion', hit, 'Job Details Clicked');
                  }
                  trackJobClick({
                    jobId: hit.objectID,
                    jobTitle: hit.normalisedTitle || hit.title || '',
                    companyId: hit.companyId || '',
                    locale: intl.locale,
                  });
                }}
                onMouseOver={(e: React.MouseEvent<HTMLAnchorElement>) => {
                  if (useBrandColour && brandColour) {
                    e.currentTarget.style.backgroundColor = brandColour;
                    e.currentTarget.style.filter = 'brightness(0.92)';
                  } else {
                    e.currentTarget.style.backgroundColor = '#6ee7b7';
                  }
                }}
                onMouseOut={(e: React.MouseEvent<HTMLAnchorElement>) => {
                  if (useBrandColour && brandColour) {
                    e.currentTarget.style.backgroundColor = brandColour;
                    e.currentTarget.style.filter = '';
                  } else {
                    e.currentTarget.style.backgroundColor = '#bbf7d0';
                  }
                }}
              >
                {intl.formatMessage({ id: 'hit.seeAd', defaultMessage: 'Details', description: 'Button label to view job details' })}
              </Link>
            </LazyTooltip>
          </div>
        </div>
        {/* Last Seen at the very bottom - use SSR-precomputed label when present to avoid Date/Intl in render */}
        {(hit._lastSeenLabel || hit.lastSeen) && (
          hit._lastSeenLabel ? (
            <div className="mt-3 text-xs text-gray-400 dark:text-gray-500 text-right flex items-center justify-end gap-1">
              <Clock className="w-3 h-3 flex-shrink-0" />
              <span>{hit._lastSeenLabel}</span>
            </div>
          ) : (
            (() => {
              const now = new Date();
              const lastSeenDate = new Date(hit.lastSeen!);
              const diffMs = now.getTime() - lastSeenDate.getTime();
              const diffMins = Math.floor(diffMs / 60000);
              const diffHours = Math.floor(diffMins / 60);
              const diffDays = Math.floor(diffHours / 24);
              let freshnessId = 'hit.lastSeen.daysAgo';
              let freshnessValue: number = diffDays;
              if (diffMins < 120) {
                freshnessId = 'hit.lastSeen.minutesAgo';
                freshnessValue = diffMins;
              } else if (diffHours < 24) {
                freshnessId = 'hit.lastSeen.hoursAgo';
                freshnessValue = diffHours;
              }
              return (
                <div className="mt-3 text-xs text-gray-400 dark:text-gray-500 text-right flex items-center justify-end gap-1">
                  <Clock className="w-3 h-3 flex-shrink-0" />
                  <FormattedMessage
                    id={freshnessId}
                    defaultMessage={
                      freshnessId === 'hit.lastSeen.minutesAgo'
                        ? '{value, plural, one {{value} minute ago} other {{value} minutes ago}}'
                        : freshnessId === 'hit.lastSeen.hoursAgo'
                        ? '{value, plural, one {{value} hour ago} other {{value} hours ago}}'
                        : '{value, plural, one {{value} day ago} other {{value} days ago}}'
                    }
                    values={{ value: freshnessValue }}
                    description="Indicates how recently the job was last seen/crawled."
                  />
                </div>
              );
            })()
          )
        )}
      </article>
    </>
  );
});

HitCard.displayName = 'HitCard';
